import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import SignedImage from '@/components/ipe/SignedImage';
import TemplateCardStampHints from '@/pages/ipe/workbench/components/TemplateCardStampHints';
import {
  loadImageTemplatePreviewUrls,
  isCanvasPreviewImageId,
  isTemplateCanvasPreviewFlagged,
  resolveImageTemplatePreviewUrl,
} from '@/pages/ipe/workbench/utils/templateListUtils';
import {
  pickImageTemplateStampAreasForImage,
  resolveTemplatePreviewImageId,
  buildStampOverlayLayout,
} from '@/pages/ipe/workbench/utils/imageTemplateStampPreview';
import {
  DEFAULT_IMAGE_TEMPLATE_SCOPE,
  filterImageTemplatesByScope,
  resolveImageTemplateScope,
  resolveVisibleImageTemplateScopes,
} from '@/pages/ipe/workbench/utils/imageTemplateAccess';
import LibraryModalPagination, {
  useLibraryModalPagination,
} from '@/pages/ipe/workbench/modals/LibraryModalPagination';
import { IMG2IMG_CASE_PAGE_SIZE_OPTIONS } from '@/pages/ipe/workbench/utils/img2imgPromptCaseLoader';

function resolveTemplatePreviewUrl(tpl, previewUrlMap) {
  return resolveImageTemplatePreviewUrl(tpl, previewUrlMap);
}

function TemplatePickerCardPreview({
  tpl,
  previewUrl,
  previewUrlMap,
  previewLoading,
  selected,
  previewSizeMap,
  onPreviewSize,
}) {
  const { t } = useTranslation('workbench');
  const previewImageId = resolveTemplatePreviewImageId(tpl);
  const hasCanvasPreview =
    isTemplateCanvasPreviewFlagged(tpl.id, previewUrlMap)
    || isCanvasPreviewImageId(tpl.previewImageId);
  const { stampList, refW, refH } = pickImageTemplateStampAreasForImage(tpl, previewImageId);
  const measured = previewSizeMap[tpl.id];
  const stampLayout = buildStampOverlayLayout(tpl, { refW, refH, measured });
  const showStampHints = Boolean(previewUrl) && stampList.length > 0 && !hasCanvasPreview;

  return (
    <div className="funding-img-tpl-picker__card-preview">
      <div className="funding-img-tpl-picker__preview-frame">
        {previewUrl ? (
          <WorkbenchPreviewImage
            className="funding-img-tpl-picker__preview-img"
            url={previewUrl}
            record={tpl}
            displayUrl={tpl.previewUrlDisplay || tpl.viewUrl || tpl.displayUrl}
            viewUrl={tpl.viewUrl || tpl.previewUrlDisplay}
            variant="full"
            alt={`${tpl.name} ${t('studioChrome.preview')}`}
            loading="eager"
            onLoad={(e) => {
              const img = e.target;
              const nw = img.naturalWidth;
              const nh = img.naturalHeight;
              if (!nw || !nh || !onPreviewSize) return;
              onPreviewSize(tpl.id, { width: nw, height: nh });
            }}
          />
        ) : (
          <span className="funding-img-tpl-picker__card-placeholder">
            {previewLoading ? t('studioChrome.loadingShort') : t('studioChrome.noPreviewShort')}
          </span>
        )}
        {showStampHints ? (
          <TemplateCardStampHints
            stampList={stampList}
            layout={stampLayout}
            keyPrefix={`${tpl.id}-picker`}
          />
        ) : null}
      </div>
      {selected ? (
        <span className="funding-img-tpl-picker__card-check" aria-hidden="true">✓</span>
      ) : null}
    </div>
  );
}

/**
 * 商品模板选择区（弹窗 / 工作流侧栏共用）
 */
export default function ImageTemplatePickerPanel({
  isActive = true,
  groups = [],
  selectedGroup = '',
  onGroupChange,
  templates = [],
  selectedTemplateId,
  selectedTemplateIds = [],
  onSelectTemplate,
  onToggleTemplate,
  selectedSources = null,
  confirming = false,
  enableScopeTabs = false,
  canAccessTeamTemplates = false,
  initialScope = DEFAULT_IMAGE_TEMPLATE_SCOPE,
  onGoCreate,
  showPagination = true,
  catalogsLoading = false,
}) {
  const { t } = useTranslation('workbench');
  const [previewUrlMap, setPreviewUrlMap] = useState({});
  const [previewSizeMap, setPreviewSizeMap] = useState({});
  const [nameQueryDraft, setNameQueryDraft] = useState('');
  const [appliedNameQuery, setAppliedNameQuery] = useState('');
  const [groupDraft, setGroupDraft] = useState('全部');
  const [appliedGroup, setAppliedGroup] = useState('全部');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [scope, setScope] = useState(initialScope || DEFAULT_IMAGE_TEMPLATE_SCOPE);
  const wasActiveRef = useRef(false);
  const gridWrapRef = useRef(null);

  const visibleScopes = useMemo(
    () => (enableScopeTabs ? resolveVisibleImageTemplateScopes(canAccessTeamTemplates) : []),
    [enableScopeTabs, canAccessTeamTemplates],
  );

  useEffect(() => {
    if (!isActive || !enableScopeTabs) return;
    const next = visibleScopes.some((item) => item.key === initialScope)
      ? initialScope
      : DEFAULT_IMAGE_TEMPLATE_SCOPE;
    setScope(next);
  }, [isActive, enableScopeTabs, initialScope, visibleScopes]);

  const effectiveSelectedIds = Array.isArray(selectedTemplateIds) && selectedTemplateIds.length > 0
    ? selectedTemplateIds
    : (selectedTemplateId ? [selectedTemplateId] : []);

  const scopedTemplates = useMemo(() => {
    if (!enableScopeTabs) return templates || [];
    return filterImageTemplatesByScope(templates, scope);
  }, [enableScopeTabs, templates, scope]);

  const groupsFromTemplates = useMemo(
    () => Array.from(new Set((scopedTemplates || []).map((t) => t?.group).filter(Boolean))),
    [scopedTemplates],
  );
  const groupOptions = useMemo(
    () => Array.from(new Set(['全部', ...groups, ...groupsFromTemplates])),
    [groups, groupsFromTemplates],
  );

  const effectiveAppliedGroup = groupOptions.includes(appliedGroup) ? appliedGroup : '全部';

  const visibleTemplates = scopedTemplates.filter((t) => {
    const matchesGroup = !effectiveAppliedGroup || effectiveAppliedGroup === '全部' || t.group === effectiveAppliedGroup;
    const matchesName = !appliedNameQuery.trim()
      || (t.name && t.name.toLowerCase().includes(appliedNameQuery.toLowerCase().trim()));
    return matchesGroup && matchesName;
  });

  const handleQuery = () => {
    const nextGroup = groupOptions.includes(groupDraft) ? groupDraft : '全部';
    setAppliedNameQuery(nameQueryDraft);
    setAppliedGroup(nextGroup);
    onGroupChange?.(nextGroup);
  };

  const handleReset = () => {
    setNameQueryDraft('');
    setAppliedNameQuery('');
    setGroupDraft('全部');
    setAppliedGroup('全部');
    onGroupChange?.('全部');
  };

  const visibleTemplateKey = useMemo(
    () => visibleTemplates.map((t) => String(t.id)).join('|'),
    [visibleTemplates],
  );

  const paginationResetKey = useMemo(
    () => `${scope}|${effectiveAppliedGroup}|${appliedNameQuery}`,
    [scope, effectiveAppliedGroup, appliedNameQuery],
  );

  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems,
    setCurrentPage,
    setPageSize,
  } = useLibraryModalPagination(visibleTemplates, {
    isActive,
    resetKey: paginationResetKey,
  });

  useEffect(() => {
    gridWrapRef.current?.scrollTo?.(0, 0);
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (!isActive) {
      setNameQueryDraft('');
      setAppliedNameQuery('');
      setGroupDraft('全部');
      setAppliedGroup('全部');
      setPreviewUrlMap({});
      setPreviewSizeMap({});
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      const initialGroup = groupOptions.includes(selectedGroup) ? selectedGroup : '全部';
      setGroupDraft(initialGroup);
      setAppliedGroup(initialGroup);
    }
    wasActiveRef.current = isActive;
  }, [isActive, selectedGroup, groupOptions]);

  const selectedIdSet = useMemo(
    () => new Set((effectiveSelectedIds || []).map((x) => String(x))),
    [effectiveSelectedIds],
  );
  const isChecked = (id) => selectedIdSet.has(String(id));
  const handleRowToggle = (tpl) => {
    if (onToggleTemplate) onToggleTemplate(tpl);
    else if (onSelectTemplate) onSelectTemplate(tpl);
  };

  const handlePreviewSize = (templateId, size) => {
    setPreviewSizeMap((prev) => {
      const cur = prev[templateId];
      if (cur?.width === size.width && cur?.height === size.height) return prev;
      return { ...prev, [templateId]: size };
    });
  };

  useEffect(() => {
    if (!isActive || visibleTemplates.length === 0) {
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);

    (async () => {
      try {
        const urlMap = await loadImageTemplatePreviewUrls(visibleTemplates);
        if (cancelled) return;
        setPreviewUrlMap((prev) => ({ ...prev, ...urlMap }));
      } catch (error) {
        console.warn('加载商品模板预览失败', error);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, visibleTemplateKey]);

  const sourceList = useMemo(
    () => (Array.isArray(selectedSources)
      ? selectedSources.filter((s) => s?.url || s?.previewUrl)
      : []),
    [selectedSources],
  );
  const showSourceStrip = sourceList.length > 0;

  const gridItems = showPagination ? paginatedItems : visibleTemplates;

  return (
    <>
      {enableScopeTabs && visibleScopes.length > 0 ? (
        <div className="funding-img-tpl-picker__scope-tabs" role="tablist" aria-label={t('imageTemplatePicker.sourceAria')}>
          {visibleScopes.map((item) => {
            const count = filterImageTemplatesByScope(templates, item.key).length;
            const active = scope === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                className={`funding-img-tpl-picker__scope-tab${active ? ' is-active' : ''}`}
                aria-selected={active}
                disabled={confirming}
                onClick={() => {
                  setScope(item.key);
                  setNameQueryDraft('');
                  setAppliedNameQuery('');
                  setGroupDraft('全部');
                  setAppliedGroup('全部');
                  onGroupChange?.('全部');
                }}
              >
                {item.key === 'personal'
                  ? t('studioChrome.personal')
                  : item.key === 'team'
                    ? t('studioChrome.team')
                    : item.key === 'official'
                      ? t('studioChrome.official')
                      : item.label}
                <em>{count}</em>
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="funding-img-tpl-picker__toolbar">
        <div className="funding-img-tpl-picker__filters" role="search">
          <div className="funding-img-tpl-picker__filter-fields">
            <div className="funding-img-tpl-picker__filter-group">
              <label
                className="funding-img-tpl-picker__filter-label"
                htmlFor="img-tpl-group"
              >
                {t('studioChrome.group')}
              </label>
              <select
                id="img-tpl-group"
                className="funding-modal__select funding-img-tpl-picker__select"
                value={groupDraft === '全部' ? '' : groupDraft}
                onChange={(e) => setGroupDraft(e.target.value || '全部')}
                disabled={confirming}
                aria-label={t('studioChrome.group')}
              >
                {groupOptions.map((g) => (
                  <option key={g} value={g === '全部' ? '' : g}>{g === '全部' ? t('studioChrome.all') : g}</option>
                ))}
              </select>
            </div>
            <div className="funding-img-tpl-picker__filter-group">
              <label
                className="funding-img-tpl-picker__filter-label"
                htmlFor="img-tpl-name-query"
              >
                {t('studioChrome.templateName')}
              </label>
              <div className="funding-img-tpl-picker__search">
                <input
                  id="img-tpl-name-query"
                  type="text"
                  className="funding-img-tpl-picker__search-input"
                  placeholder={t('studioChrome.templateNamePh')}
                  value={nameQueryDraft}
                  onChange={(e) => setNameQueryDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleQuery();
                    }
                  }}
                  autoComplete="off"
                  disabled={confirming}
                />
                {nameQueryDraft.trim() ? (
                  <button
                    type="button"
                    className="funding-img-tpl-picker__search-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNameQueryDraft('');
                    }}
                    aria-label={t('studioChrome.clearSearch')}
                    disabled={confirming}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              className="funding-img-tpl-picker__btn funding-img-tpl-picker__btn--query"
              onClick={handleQuery}
              disabled={confirming}
            >
              {t('studioChrome.query')}
            </button>
            <button
              type="button"
              className="funding-img-tpl-picker__btn funding-img-tpl-picker__btn--reset"
              onClick={handleReset}
              disabled={confirming}
            >
              {t('studioChrome.reset')}
            </button>
          </div>
        </div>
        {onGoCreate ? (
          <div className="funding-img-tpl-picker__toolbar-secondary">
            <div className="funding-img-tpl-picker__create-hint">
              <span>{t('studioChrome.noTemplate')}</span>
              <button
                type="button"
                className="funding-img-tpl-picker__create-link"
                onClick={onGoCreate}
                disabled={confirming}
              >
                {t('studioChrome.goCreate')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className="funding-img-tpl-picker__grid-wrap"
        role="region"
        aria-live="polite"
        ref={gridWrapRef}
      >
        {visibleTemplates.length === 0 ? (
          <div className="funding-img-tpl-picker__empty">
            <p>{catalogsLoading ? t('studioChrome.loadingTpl') : t('studioChrome.noMatch')}</p>
          </div>
        ) : (
          <div className="funding-img-tpl-picker__grid">
            {gridItems.map((tpl) => {
              const selected = isChecked(tpl.id);
              const previewUrl = resolveTemplatePreviewUrl(tpl, previewUrlMap);
              const scopeKey = resolveImageTemplateScope(tpl);
              const scopeLabel = scopeKey === 'personal'
                ? t('studioChrome.personal')
                : scopeKey === 'team'
                  ? t('studioChrome.team')
                  : t('studioChrome.official');
              return (
                <button
                  key={tpl.id}
                  type="button"
                  className={`funding-img-tpl-picker__card${selected ? ' is-selected' : ''}`}
                  onClick={() => {
                    if (!confirming) handleRowToggle(tpl);
                  }}
                  aria-pressed={selected}
                  aria-label={t(selected ? 'studioChrome.deselectLabeled' : 'studioChrome.selectLabeled', { label: tpl.name || tpl.id })}
                  disabled={confirming}
                >
                  <TemplatePickerCardPreview
                    tpl={tpl}
                    previewUrl={previewUrl}
                    previewUrlMap={previewUrlMap}
                    previewLoading={previewLoading}
                    selected={selected}
                    previewSizeMap={previewSizeMap}
                    onPreviewSize={handlePreviewSize}
                  />
                  {enableScopeTabs ? (
                    <span
                      className={`funding-img-tpl-picker__scope-badge funding-img-tpl-picker__scope-badge--${scopeKey}`}
                    >
                      {scopeLabel}
                    </span>
                  ) : null}
                  <span className="funding-img-tpl-picker__card-name" title={tpl.name}>
                    {tpl.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showSourceStrip ? (
        <div className="funding-img-tpl-picker__sources" aria-label={t('studioChrome.selectedPrints')}>
          <div className="funding-img-tpl-picker__sources-head">
            <span className="funding-img-tpl-picker__sources-title">{t('studioChrome.selectedPrints')}</span>
            <span className="funding-img-tpl-picker__sources-count">{t('studioChrome.sheetCount', { count: sourceList.length })}</span>
          </div>
          <div className="funding-img-tpl-picker__sources-row">
            {sourceList.map((item) => {
              const url = String(item.previewUrl || item.url || '').trim();
              return (
                <div
                  key={String(item.id || url)}
                  className="funding-img-tpl-picker__source-thumb"
                  title={item.name || item.id || ''}
                >
                  {url ? (
                    <SignedImage url={url} alt={item.name || t('studioChrome.podStamp')} loading="lazy" />
                  ) : (
                    <span>{t('studioChrome.noImage')}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {showPagination && visibleTemplates.length > 0 ? (
        <div className="funding-img-tpl-picker__pagination-bar">
          <LibraryModalPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={IMG2IMG_CASE_PAGE_SIZE_OPTIONS}
            pageSizeUnitLabel={t('studioChrome.unitItem')}
            showTotalInfo={false}
            className="funding-img-tpl-picker__pagination"
            ariaLabel={t('imageTemplatePicker.paginationAria')}
            showWhenEmpty
          />
        </div>
      ) : null}
    </>
  );
}
